import {ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, Output} from '@angular/core';
import {DetectMethodChanges} from '../utils';
import {HedgePresetClient} from './hedge-preset-client.service';
import {HedgePreset, HedgePresetOptionSide, VerticalPresetSettings, cloneHedgePreset, validHedgePreset, validHedgePresetFloor, validVerticalPreset} from './hedge-preset.model';
import {TradingInstrumentsService} from '../trading-instruments/trading-instruments-service.interface';
import {TradingInstrument} from '../trading-instruments/trading-instrument.class';

@Component({selector: 'ets-hedge-preset-designer', templateUrl: './hedge-preset-designer.component.html',
    host: {class: 'preset-designer-host'},
    styleUrls: ['./hedge-presets.scss'], changeDetection: ChangeDetectionStrategy.OnPush})
export class HedgePresetDesignerComponent implements OnChanges, OnDestroy {
    @Input() underlying: string;
    @Output() saved = new EventEmitter<void>();
    presets: HedgePreset[] = [];
    optionSideFilter: HedgePresetOptionSide = null;
    private filterRows: HedgePreset[];
    private filterSide: HedgePresetOptionSide;
    private filterUnderlying: string;
    private filteredRows: HedgePreset[] = [];
    get filteredPresets(): HedgePreset[] {
        if (this.filterRows !== this.presets || this.filterSide !== this.optionSideFilter || this.filterUnderlying !== this.underlying) {
            this.filterRows = this.presets; this.filterSide = this.optionSideFilter; this.filterUnderlying = this.underlying;
            this.filteredRows = this.presets.filter(p => p.underlying === this.underlying && p.optionSide === this.optionSideFilter);
        }
        return this.filteredRows;
    }
    selectedId = '';
    draft: HedgePreset = null;
    editing = false;
    busy = false;
    error = '';
    readonly strategies = [{id: 'butterfly', text: 'Butterfly'}, {id: 'vertical', text: 'Vertical'}];
    readonly verticalModes = [{id: 'fixed', text: 'Fixed'}, {id: 'straddlePercentage', text: 'Dynamic'}];
    private startValues = {fixed: 50, straddlePercentage: 0.4};
    private sellValues = {fixedWidth: 30, straddlePercentage: 0.6};
    readonly ivLevels = [{id: 'all', text: 'All'}];
    readonly optionSides = [{id: 'calls', text: 'Calls'}, {id: 'puts', text: 'Puts'}, {id: 'both', text: 'Calls & Puts'}];
    readonly sizingModes = [{id: 'directWidth', text: 'Fixed'}, {id: 'dynamicWidth', text: 'Dynamic'}];
    readonly numericInputAttributes = {style: 'text-align: center'};
    readonly floorValidationError = {message: 'Min Butterfly Width Must Be a Positive Multiple of 10.'};
    selectedInstrument: TradingInstrument;
    private apgWidth: number;
    private apgItems: {value: number; text: string}[] = [];
    get apgSizes(): {value: number; text: string}[] {
        const width = this.draft && this.draft.nominalApgWidth;
        if (this.apgItems.length && width === this.apgWidth) { return this.apgItems; }
        this.apgWidth = width;
        const values = [50, 100];
        if (this.draft && this.draft.nominalApgWidth > 0 && values.indexOf(this.draft.nominalApgWidth) < 0) {
            values.push(this.draft.nominalApgWidth);
        }
        this.apgItems = values.map(value => ({value, text: '$' + value}));
        return this.apgItems;
    }
    onCustomApgSize(event: {text: string; customItem?: {value: number; text: string}}): void {
        const value = Number((event.text || '').replace(/[$,]/g, '').trim());
        event.customItem = isFinite(value) && value > 0 ? {value, text: '$' + value} : null;
    }
    private epoch = 0;
    private shown = false;
    constructor(public readonly client: HedgePresetClient, private readonly _changeDetector: ChangeDetectorRef,
        private readonly instruments: TradingInstrumentsService) {}
    @DetectMethodChanges() changeUnderlying(value: TradingInstrument): void {
        if (this.editing || this.busy || !value || value.underlying === this.underlying) { return; }
        this.selectedInstrument = value;
        this.underlying = value.underlying; this.selectedId = ''; this.presets = []; this.draft = null;
        if (this.shown) { this.reload(); }
    }
    ngOnChanges(): void {
        this.selectedInstrument = this.instruments.getInstrumentByTicker(this.underlying)
            || this.instruments.getInstrumentByDisplayName(this.underlying);
        if (this.shown) { this.reload(); }
    }
    onShown(): Promise<void> {
        if (this.shown) { return Promise.resolve(); }
        this.shown = true;
        return this.reload();
    }
    ngOnDestroy(): void { ++this.epoch; }
    @DetectMethodChanges() changeOptionSide(side: HedgePresetOptionSide): void {
        if (this.busy || this.editing || side === this.optionSideFilter || !this.optionSides.some(x => x.id === side)) { return; }
        this.optionSideFilter = side;
        this.select('');
    }
    get valid(): boolean { return validHedgePreset(this.draft); }
    get floorValid(): boolean { return !this.editing || !this.draft || validHedgePresetFloor(this.draft.floor); }
    @DetectMethodChanges() updateDraft(values: Partial<HedgePreset>): void {
        Object.assign(this.draft, values);
    }
    get percentage(): number { return this.draft && this.draft.straddleProportion != null ? this.draft.straddleProportion * 100 : null; }
    get butterflyAnchorPercentage(): number {
        return this.draft.offsetProportion == null ? null : this.draft.offsetProportion * 100;
    }
    @DetectMethodChanges() setButterflyAnchorPercentage(value: number): void {
        if (!this.editing || this.busy) { return; }
        this.draft.offsetProportion = value == null ? null : value / 100;
    }
    get butterflyError(): string {
        if (!this.draft || this.draft.strategy !== 'butterfly' || this.draft.sizingMode !== 'dynamicWidth') { return ''; }
        const p = this.draft;
        const validSettings = typeof p.straddleProportion === 'number' && isFinite(p.straddleProportion) && p.straddleProportion > 0
            && typeof p.offsetProportion === 'number' && isFinite(p.offsetProportion)
            && typeof p.minOffset === 'number' && isFinite(p.minOffset) && p.minOffset >= 0
            && validHedgePresetFloor(p.floor);
        return validSettings ? '' :
            'Enter Body and Anchor Percentages, a Nonnegative Min Offset and a Positive Min Width in Multiples of Ten.';
    }
    @DetectMethodChanges() setPercentage(value: string | number | null): void {
        this.draft.straddleProportion = value == null || value === '' ? null : Number(value) / 100;
    }
    @DetectMethodChanges() setStrategy(strategy: HedgePreset['strategy']): void {
        if (!this.draft || !this.editing || this.busy || this.strategies.every(s => s.id !== strategy)) { return; }
        this.draft.strategy = strategy;
        if (strategy === 'vertical' && !this.draft.verticalSettings) {
            this.draft.verticalSettings = {startMode: 'straddlePercentage', startValue: 0.4,
                sellMode: 'straddlePercentage', sellValue: 0.6, minOffset: null, maxOffset: null, minWidth: null, maxWidth: null};
        }
    }
    @DetectMethodChanges() updateVertical(values: Partial<VerticalPresetSettings>): void {
        if (this.editing && !this.busy && this.draft && this.draft.verticalSettings) {
            this.draft.verticalSettings = {...this.draft.verticalSettings, ...values};
        }
    }
    get verticalStartValue(): number {
        const v = this.draft && this.draft.verticalSettings;
        return !v || v.startValue == null ? null : v.startValue * (v.startMode === 'straddlePercentage' ? 100 : 1);
    }
    get verticalSellValue(): number {
        const v = this.draft && this.draft.verticalSettings;
        return !v || v.sellValue == null ? null : v.sellValue * (v.sellMode === 'straddlePercentage' ? 100 : 1);
    }
    get verticalStartFormat(): string { return this.draft.verticalSettings.startMode === 'straddlePercentage' ? "#0.##'%'" : '#0.##'; }
    get verticalSellFormat(): string { return this.draft.verticalSettings.sellMode === 'straddlePercentage' ? "#0.##'%'" : '#0.##'; }
    setVerticalStartValue(value: number): void {
        this.updateVertical({startValue: value == null ? null : value / (this.draft.verticalSettings.startMode === 'straddlePercentage' ? 100 : 1)});
    }
    setVerticalSellValue(value: number): void {
        this.updateVertical({sellValue: value == null ? null : value / (this.draft.verticalSettings.sellMode === 'straddlePercentage' ? 100 : 1)});
    }
    @DetectMethodChanges() setVerticalMode(mode: VerticalPresetSettings['startMode']): void {
        if (!this.editing || this.busy || !this.draft || !this.draft.verticalSettings
            || !this.verticalModes.some(x => x.id === mode)) { return; }
        const v = this.draft.verticalSettings;
        this.startValues[v.startMode] = v.startValue;
        this.sellValues[v.sellMode] = v.sellValue;
        const sellMode = mode === 'fixed' ? 'fixedWidth' : 'straddlePercentage';
        this.updateVertical({startMode: mode, startValue: this.startValues[mode],
            sellMode, sellValue: this.sellValues[sellMode]});
    }
    get verticalError(): string {
        const v = this.draft && this.draft.strategy === 'vertical' && this.draft.verticalSettings;
        if (!v) { return ''; }
        if (v.startMode === 'straddlePercentage' && v.minOffset != null && v.maxOffset != null && v.minOffset > v.maxOffset) {
            return 'Min Offset Must Not Exceed Max Offset.';
        }
        if (v.sellMode === 'straddlePercentage' && v.minWidth != null && v.maxWidth != null && v.minWidth > v.maxWidth) {
            return 'Min Width Must Not Exceed Max Width.';
        }
        if (v.startMode === 'straddlePercentage' && v.sellMode === 'straddlePercentage' && v.sellValue <= v.startValue) {
            return 'Sell Percentage Must Exceed Start Percentage.';
        }
        return validVerticalPreset(v) ? '' : 'Enter Valid Distances and Limits. Width and Maximum Limits Must Be Positive.';
    }
    @DetectMethodChanges({isAsync: true}) async reload(): Promise<void> {
        const epoch = ++this.epoch;
        this.busy = true; this.error = '';
        this._changeDetector.detectChanges();
        try {
            const rows = await this.client.list(this.underlying);
            if (epoch !== this.epoch) { return; }
            this.presets = rows;
            this.select(this.selectedId && this.filteredPresets.some(p => p.id === this.selectedId) ? this.selectedId : '');
        } catch (e) { if (epoch === this.epoch) { this.error = e.message || 'Could Not Load Presets.'; } }
        finally { if (epoch === this.epoch) { this.busy = false; } }
    }
    @DetectMethodChanges() select(id: string): void {
        const item = this.filteredPresets.filter(p => p.id === id)[0];
        this.selectedId = item ? id : '';
        this.draft = item ? cloneHedgePreset(item) : null; this.editing = false; this.error = '';
        this.startValues = {fixed: 50, straddlePercentage: 0.4};
        this.sellValues = {fixedWidth: 30, straddlePercentage: 0.6};
    }
    @DetectMethodChanges() create(): void {
        if (this.busy || this.editing || !this.underlying || !this.optionSideFilter) { return; }
        // Full UUID required by the catalogue, rather than the UI's shortened run IDs.
        const bytes = new Uint8Array(16); window.crypto.getRandomValues(bytes);
        bytes[6] = (bytes[6] & 15) | 64; bytes[8] = (bytes[8] & 63) | 128;
        const hex = Array.prototype.map.call(bytes, b => ('0' + b.toString(16)).slice(-2)).join('');
        const id = hex.slice(0, 8) + '-' + hex.slice(8, 12) + '-' + hex.slice(12, 16) + '-' + hex.slice(16, 20) + '-' + hex.slice(20);
        this.draft = {id, underlying: this.underlying, name: '', optionSide: this.optionSideFilter, revision: 0, nominalApgWidth: 50,
            strategy: 'butterfly', ivLevel: 'all', sizingMode: 'directWidth', offset: 50, width: 50,
            straddleProportion: 1, offsetProportion: .4, minOffset: 50, floor: 50};
        this.editing = true; this.error = '';
    }
    @DetectMethodChanges() edit(): void {
        this.editing = true;
        if (this.draft.strategy === 'butterfly' && this.draft.requiresReconfiguration) {
            if (this.draft.minOffset == null) { this.draft.minOffset = 50; }
            if (this.draft.floor == null) { this.draft.floor = 50; }
        }
    }
    @DetectMethodChanges() cancel(): void { this.select(this.selectedId); }
    @DetectMethodChanges() setMode(mode: 'directWidth' | 'dynamicWidth'): void {
        this.draft.sizingMode = mode;
        if (mode === 'dynamicWidth') {
            if (this.draft.straddleProportion == null) { this.draft.straddleProportion = 1; }
            if (this.draft.floor == null) { this.draft.floor = 50; }
            if (this.draft.offsetProportion == null) { this.draft.offsetProportion = .4; }
            if (this.draft.minOffset == null) { this.draft.minOffset = 50; }
        } else if (this.draft.width == null) { this.draft.width = 50; }
    }
    @DetectMethodChanges({isAsync: true}) async save(): Promise<void> {
        if (!this.editing || !this.valid || this.busy) { return; }
        const draft = cloneHedgePreset(this.draft); const epoch = this.epoch;
        this.busy = true; this.error = ''; this._changeDetector.detectChanges();
        try {
            await this.client.save(draft);
            if (epoch !== this.epoch) { return; }
            this.optionSideFilter = draft.optionSide;
            this.selectedId = draft.id; await this.reload(); this.saved.emit();
        }
        catch (e) { if (epoch === this.epoch) { this.error = e.message || 'Could Not Save Preset.'; this.busy = false; } }
    }
    @DetectMethodChanges({isAsync: true}) async remove(): Promise<void> {
        if (!this.draft || !this.draft.revision || this.busy) { return; }
        const draft = {...this.draft}; const epoch = this.epoch;
        this.busy = true; this.error = ''; this._changeDetector.detectChanges();
        try {
            await this.client.remove(draft);
            if (epoch !== this.epoch) { return; }
            this.selectedId = ''; await this.reload(); this.saved.emit();
        }
        catch (e) { if (epoch === this.epoch) { this.error = e.message || 'Could Not Delete Preset.'; this.busy = false; } }
    }
}
